import { useCurrentUser } from '@cma/app'
import { Alert, toast } from '@cma/common'
import {
  PlatformClientContentPartsFragment,
  PlatformClientFolderPartsFragment
} from '@cma/generated/graphql'
import {
  FloatingActionBar,
  FloatingActionBarItem
} from '@helix/floating-action-bar'
import {
  inbox as DownloadIcon,
  share as ShareIcon,
  trash_can as TrashIcon
} from '@helix/helix-icon/outlined'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import pluralize from 'pluralize'
import { useMemo, useState } from 'react'
import { usePlatformClient } from '../usePlatformClient'
import { useContent } from './ContentContext'
import { ShareContentModal } from './share_modal/ShareContentModal'
import { useDestroyPlatformClientContents } from './useDestroyPlatformClientContents'
import { useDestroyPlatformClientFolders } from './useDestroyPlatformClientFolders'
import { getFileNameFromUrl } from './utils'

type PlatformClientItem =
  | PlatformClientContentPartsFragment
  | PlatformClientFolderPartsFragment

type ZipFolder = {
  folder: JSZip | null
  name: string
  link: string
}

export function CheckedContentFloatingActionBar({
  filteredContents
}: {
  filteredContents: Array<PlatformClientItem>
}) {
  const { data: { currentUser } = {} } = useCurrentUser()
  const { data: { platformClient } = {} } = usePlatformClient({
    id: currentUser?.account?.id || ''
  })

  const { mutateAsync: destroyContents, isLoading: isDeletingContents } =
    useDestroyPlatformClientContents()

  const { mutateAsync: destroyFolders, isLoading: isDeletingFolders } =
    useDestroyPlatformClientFolders()

  const {
    currentFolder,
    selectedContents,
    hasContentPermissions,
    deselectAllFiles,
    setIsDeleting
  } = useContent()

  const [showSharingModal, setShowSharingModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const selectedSet = useMemo(
    () => new Set(selectedContents),
    [selectedContents]
  )

  const filteredSelectedContents = useMemo(
    () =>
      filteredContents?.filter((file: PlatformClientItem) =>
        selectedSet.has(file.id)
      ) || [],
    [filteredContents, selectedSet]
  )

  const selectedCount = filteredSelectedContents.length
  const isDeleting = isDeletingContents || isDeletingFolders

  const handleShare = () => {
    setShowSharingModal(true)
  }

  const handleDelete = async () => {
    if (!filteredSelectedContents.length || isDeleting) return

    const selectedFileIds = filteredSelectedContents
      .filter((item: PlatformClientItem) => item.__typename !== 'Folder')
      .map((item: PlatformClientItem) => item.id)

    const selectedFolderIds = filteredSelectedContents
      .filter((item: PlatformClientItem) => item.__typename === 'Folder')
      .map((item: PlatformClientItem) => item.id)

    setIsDeleting(true)
    setShowDeleteModal(false)

    const promises: Promise<unknown>[] = []

    if (selectedFileIds.length > 0) {
      promises.push(destroyContents({ ids: selectedFileIds }))
    }

    if (selectedFolderIds.length > 0) {
      promises.push(destroyFolders({ ids: selectedFolderIds }))
    }

    try {
      const results = await Promise.allSettled(promises)

      const failures = results.filter((result) => result.status === 'rejected')

      if (failures.length === 0) {
        toast('Items deleted successfully', {
          variant: 'success'
        })
      } else {
        toast('Some items could not be deleted', {
          variant: 'error'
        })
      }
    } finally {
      setIsDeleting(false)
      deselectAllFiles()
    }
  }

  const handleDownload = async () => {
    const selectedFiles = filteredSelectedContents.filter(
      (file: PlatformClientItem): file is PlatformClientContentPartsFragment =>
        file.__typename === 'PlatformClientContent'
    )

    const selectedFolders = filteredSelectedContents.filter(
      (file: PlatformClientItem): file is PlatformClientFolderPartsFragment =>
        file.__typename === 'Folder'
    )

    const sanitizeFolderName = (name: string) =>
      (name || 'Folder').trim().replace(/[\\/:*?"<>|]/g, '_') || 'Folder'

    const rootFolderName =
      platformClient?.platformAccountName?.trim() || 'Contents'

    const zip = new JSZip()
    const rootFolder = zip.folder(sanitizeFolderName(rootFolderName))
    const entries: ZipFolder[] = []

    selectedFiles.forEach((file) => {
      if (!file.platformClientContentUrl) return

      entries.push({
        folder: rootFolder,
        name: getFileNameFromUrl({
          displayName: file.displayName,
          url: file.platformClientContentUrl
        }),
        link: file.platformClientContentUrl
      })
    })

    selectedFolders.forEach((folderItem) => {
      const pages = folderItem.customPages || []
      if (!pages.length) return

      const subFolder = rootFolder?.folder(
        sanitizeFolderName(folderItem.displayName)
      )

      pages.forEach((page) => {
        if (!page.platformClientContentUrl) return

        entries.push({
          folder: subFolder || null,
          name: getFileNameFromUrl({
            displayName: page.displayName,
            url: page.platformClientContentUrl
          }),
          link: page.platformClientContentUrl
        })
      })
    })

    if (!entries.length) {
      toast('No downloadable files selected', {
        variant: 'warning'
      })
      return
    }

    let addedCount = 0
    const failedFiles: string[] = []

    for (const { folder, name, link } of entries) {
      if (!folder || !link) continue

      try {
        const res = await fetch(link)
        if (!res.ok) throw new Error(`Failed to download: ${name}`)
        const blob = await res.blob()
        folder.file(name, blob)
        addedCount++
      } catch {
        failedFiles.push(name)
      }
    }

    if (!addedCount) {
      toast('No files could be downloaded', {
        variant: 'error'
      })
      return
    }

    if (failedFiles.length > 0) {
      toast(`Some files couldn't be downloaded: ${failedFiles.join(', ')}`, {
        variant: 'warning'
      })
    }

    const zipBlob = await zip.generateAsync({ type: 'blob' })
    saveAs(zipBlob, `${sanitizeFolderName(rootFolderName)}.zip`)
  }

  if (!filteredSelectedContents.length) {
    return null
  }

  return (
    <>
      <FloatingActionBar
        show={selectedCount > 0 && !isDeleting}
        itemsSelected={selectedCount}
        onDismiss={deselectAllFiles}>
        <FloatingActionBarItem
          icon={DownloadIcon}
          id="download-action-bar"
          label="Download"
          onClick={handleDownload}
        />
        {hasContentPermissions && (
          <>
            {!currentFolder && (
              <FloatingActionBarItem
                icon={ShareIcon}
                id="share-action-bar"
                label="Share"
                onClick={handleShare}
              />
            )}
            <FloatingActionBarItem
              icon={TrashIcon}
              id="delete-action-bar"
              label="Delete"
              onClick={() => setShowDeleteModal(true)}
            />
          </>
        )}
      </FloatingActionBar>
      {hasContentPermissions && (
        <>
          <ShareContentModal
            ids={filteredSelectedContents.map(
              (
                content:
                  | PlatformClientContentPartsFragment
                  | PlatformClientFolderPartsFragment
              ) => ({
                id: content.id,
                type: content.__typename
              })
            )}
            open={showSharingModal}
            onClose={() => setShowSharingModal(false)}
          />
          <Alert variant="danger" isOpen={showDeleteModal}>
            <Alert.Title>Delete Items</Alert.Title>
            <Alert.Content>
              Are you sure you want to delete{' '}
              <strong className="font-medium text-gray-700">
                {selectedCount} {pluralize('item', selectedCount)}
              </strong>
              ? This action cannot be undone. The selected items will be
              permanently deleted.
            </Alert.Content>
            <Alert.Cancel onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Alert.Cancel>
            <Alert.Confirm loading={isDeleting} onClick={handleDelete}>
              Delete Forever
            </Alert.Confirm>
          </Alert>
        </>
      )}
    </>
  )
}
